import { Paper } from '@material-ui/core';
import {
  StyleRules,
  Theme,
  createTheme as createMuiTheme,
} from '@material-ui/core/styles';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';

import { BackstageOverrides } from '@backstage/core-components';
import {
  BackstageTheme,
  PageTheme,
  createTheme,
  darkTheme,
  pageTheme as defaultPageThemes,
  lightTheme,
} from '@backstage/theme';

import { IconChevronRight } from '@spotify/confidence-core-react';
// best way to get the Spotify fonts?
import '@spotify/confidence-encore-foundation/css/fonts.css';

import { BuildBuiThemeOptions } from './types';

/**
 * Builds a MUI v4 theme that matches the BUI (Backstage UI) design system.
 *
 * This function uses BUI CSS variables for component overrides, which allows
 * the theme to automatically adapt to light/dark mode changes. The palette
 * uses hex values for MUI's internal calculations.
 */
export const buildBuiTheme = (
  options: BuildBuiThemeOptions,
): BackstageTheme => {
  const { palette = {}, type = 'light' } = options;

  // Create page themes with BUI-compatible font colors
  const pageThemesFontColorOverride: Record<string, PageTheme> = {};
  Object.keys(defaultPageThemes).map(key => {
    pageThemesFontColorOverride[key] = {
      ...defaultPageThemes[key],
      fontColor: 'var(--bui-fg-primary)',
    };
  });

  const base: Theme = type === 'dark' ? darkTheme : lightTheme;
  const baseTheme = createTheme({
    // We need to include some of this in the base theme,
    // since we're passing this theme when we're creating overrides.
    // and many of the overrides are using typography by spreading
    // the base theme's typography.
    //
    typography: {
      ...base.typography,
      // @ts-expect-error - CSS variable not compatible with MUI's string type
      body1: {
        fontFamily: 'var(--encore-body-font-stack)',
        ...base.typography.body1,
      },
      body2: {
        fontFamily: 'var(--encore-body-font-stack)',
        ...base.typography.body2,
      },
      h1: {
        fontSize: 'var(--bui-font-size-7)',
        fontFamily: 'var(--encore-title-font-stack)',
        fontWeight: 600,
        marginBottom: 0,
      },
      h2: {
        fontSize: 'var(--bui-font-size-6)',
        fontFamily: 'var(--encore-title-font-stack)',
        fontWeight: 600,
        marginBottom: 0,
      },
      h3: {
        fontFamily: 'var(--encore-title-font-stack)',
        fontSize: 'var(--bui-font-size-5)',
        fontWeight: 600,
        marginBottom: 0,
      },
      h4: {
        fontFamily: 'var(--encore-title-font-stack)',
        fontSize: 'var(--bui-font-size-4)',
        fontWeight: 600,
        marginBottom: 0,
      },
      h5: {
        fontFamily: 'var(--encore-title-font-stack)',
        fontSize: 'var(--bui-font-size-3)',
        fontWeight: 600,
        marginBottom: 0,
      },
      h6: {
        fontFamily: 'var(--encore-title-font-stack)',
        fontSize: 'var(--bui-font-size-2)',
        fontWeight: 600,
        marginBottom: 0,
      },
      subtitle1: {
        ...base.typography.subtitle1,
        fontFamily: 'var(--encore-body-font-stack)',
        fontSize: 'var(--bui-font-size-2)',
      },
      subtitle2: {
        ...base.typography.subtitle2,
        fontFamily: 'var(--encore-body-font-stack)',
        fontSize: 'var(--bui-font-size-2)',
      },
      htmlFontSize: 16,
    },
    palette: {
      ...base.palette,
      ...palette,
      status: {
        ok: 'var(--bui-fg-success)',
        warning: 'var(--bui-fg-warning)',
        error: 'var(--bui-fg-danger)',
        running: 'var(--bui-fg-link)',
        aborted: palette.text?.disabled || base.palette.status.aborted,
        pending: palette.text?.disabled || base.palette.status.pending,
        ...palette.status,
      },
      primary: {
        main: '#1ed760', // Spotify green
        light: '#3be477',
        dark: '#1abc54',
        contrastText: '#000000', // Black on green
        ...palette.primary,
      },
      error: {
        main: '#e22b2b',
        dark: '#e22b2b',
        light: '#f87a7a',
        contrastText: '#ffffff',
        ...palette.error,
      },
      warning: {
        main: '#e36d05',
        dark: '#e36d05',
        light: '#ffa42b',
        contrastText: '#000000',
        ...palette.warning,
      },
      info: {
        main: '#0d72ea',
        dark: '#0d72ea',
        light: '#0C6DDF',
        contrastText: '#fff',
        ...palette.info,
      },
      success: {
        main: '#1db954',
        dark: '#1db954',
        light: '#53db83',
        contrastText: '#000000',
        ...palette.success,
      },
    },
    // fontFamily: 'var(--bui-font-regular)',
    fontFamily:
      'var(--encore-body-font-stack), CircularSp,CircularSp-Arab,CircularSp-Hebr,CircularSp-Cyrl,CircularSp-Grek,CircularSp-Deva, var(--fallback-fonts, sans-serif)',
    pageTheme: pageThemesFontColorOverride,
    defaultPageTheme: 'home',
  });

  // FIXME: UGLY HACK DELUX
  baseTheme.spacing = createMuiTheme({ spacing: 4 }).spacing;

  const createCustomThemeOverrides = (
    theme: BackstageTheme,
  ): BackstageOverrides & Partial<StyleRules> => {
    // BUI Button styles
    const primaryButton = {
      backgroundColor: 'var(--bui-bg-solid)',
      color: 'var(--bui-fg-solid)',
      border: 'none',
      '&:hover': {
        backgroundColor: 'var(--bui-bg-solid-hover)',
      },
      '&:active': {
        backgroundColor: 'var(--bui-bg-solid-pressed)',
      },
      '&.Mui-disabled': {
        backgroundColor: 'var(--bui-bg-solid)',
        color: 'var(--bui-fg-solid)',
        opacity: 0.5,
      },
    };

    const secondaryButton = {
      backgroundColor: 'var(--bui-bg-surface-1)',
      boxShadow: 'inset 0 0 0 1px var(--bui-border)',
      color: 'var(--bui-fg-primary)',
      border: 'none',
      '&:hover': {
        boxShadow: 'inset 0 0 0 1px var(--bui-border-hover)',
        backgroundColor: 'var(--bui-bg-surface-1)',
      },
      '&:active': {
        boxShadow: 'inset 0 0 0 1px var(--bui-border-pressed)',
      },
      '&.Mui-disabled': {
        boxShadow: 'inset 0 0 0 1px var(--bui-border-disabled)',
        color: 'var(--bui-fg-disabled)',
      },
    };

    const tertiaryButton = {
      backgroundColor: 'transparent',
      color: 'var(--bui-fg-primary)',
      border: 'none',
      boxShadow: 'none',
      '&:hover': {
        backgroundColor: 'var(--bui-bg-surface-1)',
      },
      '&:active': {
        backgroundColor: 'var(--bui-bg-surface-2)',
      },
      '&.Mui-disabled': {
        backgroundColor: 'transparent',
        color: 'var(--bui-fg-disabled)',
      },
    };

    return {
      AppHeaderRoot: {
        root: {
          borderBottom: 'none',
          background: theme.palette.background.paper,
        },
      },
      AppHeaderBrand: {
        root: {
          padding: theme.spacing(4),
        },
        text: {
          fontSize: 'var(--bui-font-size-4)',
        },
      },
      AppSearchBar: {
        root: {
          border: 'none',
          background: 'var(--bui-bg-surface-1)',
        },
        placeholder: {
          fontSize: 'var(--bui-font-size-4)',
        },
        icon: {
          marginLeft: theme.spacing(2),
        },
        hotkey: {
          marginRight: theme.spacing(2),
          backgroundColor: 'var(--bui-bg-surface-1)',
        },
      },
      FloatingHeader: {
        container: {
          padding: theme.spacing(1, 4),
          background: theme.palette.background.default,
        },
      },
      ActionBar: {
        content: {
          // @ts-expect-error - custom component override not in MUI types
          paddingTop: 'var(--bui-space-4)',
        },
      },
      BackstageHeader: {
        header: {
          backgroundImage: 'unset',
          boxShadow: 'unset',
          color: 'var(--bui-fg-primary)',
          paddingBottom: theme.spacing(1),
          padding: theme.spacing(4),
          marginLeft: 'auto',
          marginRight: 'auto',
          maxWidth: theme.breakpoints.values.xl,
          [theme.breakpoints.down('md')]: {
            flexWrap: 'nowrap',
          },
        },
        breadcrumb: {
          marginBottom: theme.spacing(2),
        },
        title: {
          color: 'var(--bui-fg-primary)',
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
          overflow: 'hidden',
          ...theme.typography.h1,
        },
        subtitle: {
          color: 'var(--bui-fg-secondary)',
        },
        type: {
          color: 'var(--bui-fg-secondary)',
        },
        leftItemsBox: {
          overflow: 'hidden',
          flexGrow: 'initial',
          '&:has(+ $rightItemsBox:empty)': {
            flexGrow: 1,
          },
        },
        rightItemsBox: {
          gap: theme.spacing(1),
          alignItems: 'center',
          paddingLeft: theme.spacing(2),
          flexGrow: 1,
          paddingRight: theme.spacing(2),
          '&:empty': {
            display: 'none',
          },
        },
      },
      BackstageContent: {
        root: {
          width: '100%',
          maxWidth: theme.breakpoints.values.xl,
          marginLeft: 'auto',
          marginRight: 'auto',
          paddingTop: theme.spacing(2),
          'div:has([role=tablist]) + &': {
            paddingTop: theme.spacing(4),
          },
          padding: theme.spacing(4, 3, 3, 4),
          [theme.breakpoints.up('md')]: {
            padding: theme.spacing(4, 4, 3, 4),
          },

          // Hack for gap between sidebar and content on workflow instance pages
          '& [class*="BackstageContent-wrapper"] [class*="BackstageContent-container"]':
            {
              gap: theme.spacing(4),
            },
          '& [class*="SidebarContent-container"]': {
            gap: theme.spacing(6),
          },
        },
      },
      BackstagePage: {
        root: {
          flex: 1,
          height: 'unset',
          scrollbarGutter: 'stable',
          overflow: 'hidden',
          background: theme.palette.background.default,
          borderRadius: 'var(--bui-radius-4)',

          gridTemplateRows: 'max-content max-content auto 1fr',
          gridTemplateAreas:
            "'pageFloatingHeader pageFloatingHeader pageFloatingHeader'" +
            "'pageHeader pageHeader pageHeader'" +
            "'pageSubheader pageSubheader pageSubheader'" +
            "'pageNav pageContent pageSidebar'",
        },
      },
      BackstageSidebarPage: {
        root: {
          paddingLeft: 0,
          [theme.breakpoints.up('sm')]: {
            paddingLeft: 0,
          },

          paddingTop: 64,
          paddingRight: 16,
          paddingBottom: 16,
          height: 'calc(100vh)',
          overscrollBehavior: 'contain',
          background: theme.palette.background.paper,

          padding: 8,
          display: 'flex',
        },
      },
      BackstageSidebar: {
        // @ts-expect-error - Backstage override not in MUI types
        root: {
          position: 'relative',
          height: '100%',
          display: 'flex',
          background: theme.palette.background.paper,
        },
        drawer: {
          // Cancel some Backstage styling
          top: 'unset',
          left: 'unset',
          bottom: 'unset',
          right: 'unset',
          position: 'relative',
          flexGrow: 1,

          paddingTop: 12,
          paddingBottom: 12,
          paddingRight: 'var(--bui-space-3)',
          paddingLeft: 'var(--bui-space-3)',

          margin: theme.spacing(0, 3, 0, 3),

          width: '56px !important',
          borderRadius: 'var(--bui-radius-4)',

          // background: 'var(--bui-bg-surface-1)'
          background: theme.palette.background.default,

          '&$drawerOpen': {
            width: '250px !important',
          },
          '& > hr': {
            display: 'none',
          },
        },
      },
      BackstageSidebarDivider: {
        root: {
          background: 'green',
          // display: 'none',
        },
      },
      BackstageSidebarItem: {
        root: {
          display: 'inline-flex',
          borderRadius: 'var(--bui-radius-2)',
          '&$selected$selected': {
            borderLeft: 'none',
          },
          height: 36,
          margin: '1px 0px',
          '&$selected$closed': {
            width: '32px !important',
          },
          '&$closed': {
            width: '32px !important',
          },
        },
        selected: {
          backgroundColor: 'var(--bui-bg-surface-1)',
        },
        label: {
          fontSize: 'var(--bui-font-size-3)',
          fontWeight: 400,
          '$root$selected:has(+ a[aria-current=page]) &': {
            color: 'var(--bui-fg-secondary)',
            fontWeight: 'normal',
          },
          '$selected &, [data-testid*="item-with-submenu"][class]:not([class=""]) &, $root$highlightable:hover &, $root$selected:has(+ a[aria-current=page]):hover &':
            {
              // fontWeight: 'bold',
              color: 'var(--bui-fg-primary)',
            },
        },
        highlightable: {
          '&:hover': {
            backgroundColor: 'var(--bui-bg-tint-hover)',
            color: 'var(--bui-fg-primary)',
          },
          '&$selected': {
            backgroundColor: 'var(--bui-bg-tint-pressed)',
            color: 'var(--bui-fg-tint)',
          },
        },
        highlighted: {
          backgroundColor: 'var(--bui-bg-surface-1)',
        },
        buttonItem: {
          '&$highlightable:hover': {
            transform: 'scale(1)',
            backgroundColor: 'var(--bui-bg-surface-1)',
          },
        },
        iconContainer: {
          height: 24,
          width: 24,
          minWidth: 24,
          marginRight: 'var(--bui-space-2)',
          marginLeft: 'var(--bui-space-1)',
          borderRadius: 'var(--bui-radius-1)',
          transition: theme.transitions.create(['background-color', 'color'], {
            duration: '0.1s',
          }),
          '$root$selected &': {
            marginLeft: 'var(--bui-space-1)',
            // backgroundColor: 'var(--bui-bg-solid)',
            // color: 'var(--bui-fg-solid)',
          },
          '$root$selected:has(+ a[aria-current=page]) &': {
            backgroundColor: 'var(--bui-bg-surface-1)',
            color: 'var(--bui-fg-secondary)',
          },
          '$root$selected:hover &, $root$selected:has(+ a[aria-current=page]):hover &':
            {
              // color: 'var(--bui-fg-solid)',
            },
          '$root:hover &, $root$selected:has(+ a[aria-current=page]):hover &': {
            color: 'var(--bui-fg-primary)',
          },

          '& > span > div > svg': {
            width: 16,
            height: 16,
          },
        },
        closed: {
          marginLeft: 0,
          // width: '100%',
          minWidth: 'auto',
          '&$selected$root': {
            // width: '100%',
          },
          '& + div:last-child h5': {
            display: 'block',
          },
        },
        open: {
          [theme.breakpoints.up('sm')]: {
            // width: '100%',
          },
        },
        closedItemIcon: {
          height: 24,
          width: 24,
          borderRadius: 'var(--bui-radius-2)',
          display: 'flex',
          alignItems: 'center',
          transition: theme.transitions.create(['background-color', 'color'], {
            duration: '0.1s',
          }),

          '$root$selected &': {
            paddingRight: 0,
            // backgroundColor: 'var(--bui-bg-solid)',
            color: 'var(--bui-fg-primary)',
          },
          '$root:hover &': {
            color: 'var(--bui-fg-primary)',
          },
          '$root$selected:hover &': {
            color: 'var(--bui-fg-solid)',
          },
          '$root$selected:has(+ a[aria-current=page]) &': {
            backgroundColor: 'var(--bui-bg-surface-1)',
            color: 'var(--bui-fg-secondary)',
          },

          '& > div': {
            marginLeft: '0 !important',
          },

          '& > div > svg': {
            width: 16,
            height: 16,
            '& + svg': {
              display: 'none',
            },
          },
        },
        secondaryAction: {
          width: 'auto',
        },
        submenuArrow: {
          display: 'none',
        },
        searchContainer: {
          width: '100%',
          margin: 0,
        },
        searchField: {
          fontWeight: 'normal',
          color: 'var(--bui-fg-secondary)',
        },
        searchFieldHTMLInput: {
          padding: 'var(--bui-space-3) var(--bui-space-2)',
        },
        expandButton: {
          '&:hover': {
            transform: 'scale(1)',
          },
        },
      },
      BackstageSidebarSubmenu: {
        title: {
          display: 'none',
          color: 'var(--bui-fg-primary)',
          padding: 'var(--bui-space-1) var(--bui-space-4) var(--bui-space-2)',
          ...theme.typography.subtitle1,
          fontWeight: 'bold',
        },
      },
      BackstageSidebarSubmenuItem: {
        item: {
          height: 'auto',
          borderRadius: 0,
          padding: 'var(--bui-space-2)',
          '& > svg, & > span > svg:first-child': {
            display: 'none',
          },
          '&:hover': {
            backgroundColor: 'transparent',
            transform: 'scale(1)',
          },
          justifyContent: 'flex-start',
        },
        itemContainer: {
          [theme.breakpoints.down('sm')]: {
            paddingLeft: theme.spacing(4),
          },
        },
        dropdownItem: {
          '&:hover': {
            backgroundColor: 'transparent',
            '& $textContent': {
              color: 'var(--bui-fg-primary)',
              fontWeight: 'bold',
            },
          },
        },
        label: {
          ...theme.typography.body2,
          margin: 0,
          '$item:hover &, $selected &': {
            fontWeight: 'bold',
            color: 'var(--bui-fg-primary)',
          },
        },
        selected: {
          '&$item': {
            backgroundColor: 'transparent',
          },
        },
      },
      BackstageHeaderTabsContainer: {
        backgroundColor: theme.palette.background.paper,
      },
      BackstageHeaderTabs: {
        defaultTab: {
          textTransform: 'none',
          padding: 'var(--bui-space-3) 0',
          fontSize: 'var(--bui-font-size-3)',
          fontWeight: 400,
        },
        tabsWrapper: {
          backgroundColor: 'transparent',
          width: '100%',
          maxWidth: theme.breakpoints.values.xl,
          marginLeft: 'auto',
          marginRight: 'auto',
          paddingLeft: 'var(--bui-space-4)',
        },
      },
      BackstageBreadcrumbsStyledBox: {
        root: {
          textDecoration: 'none',
        },
      },
      BackstageOpenedDropdown: {
        icon: {
          '& path': {
            fill: 'var(--bui-fg-primary)',
          },
        },
      },
      BackstageTabbedCard: {
        indicator: {
          backgroundColor: 'var(--bui-fg-primary)',
        },
      },
      BackstageTable: {
        root: {
          '&> :first-child': {
            borderBottom: '1px solid var(--bui-border)',
            boxShadow: 'none',
          },
          '& th': {
            borderTop: 'none',
            textTransform: 'none !important',
          },
        },
      },

      MuiAlert: {
        root: {
          border: 0,
          borderRadius: 'var(--bui-radius-3)',
          padding: theme.spacing(2),
          '&$standardSuccess, &$standardError, &$standardInfo, &$standardWarning ':
            {
              backgroundColor: 'var(--bui-bg-surface-2)',
              color: 'var(--bui-fg-primary)',
            },
          '&.RuleDetailAlert': {
            backgroundColor: 'var(--bui-bg-surface-1)',
            '& .RuleDetailBox': {
              backgroundColor: 'var(--bui-bg-surface-2)',
            },
          },
        },
        icon: {
          color: 'inherit',
        },
        standardSuccess: {
          '& $icon': {
            color: 'var(--bui-fg-success)',
          },
        },
        standardWarning: {
          '& $icon': {
            color: 'var(--bui-fg-warning)',
          },
        },
        standardError: {
          '& $icon': {
            color: 'var(--bui-fg-danger)',
          },
        },
        filledSuccess: {
          backgroundColor: 'var(--bui-bg-success)',
          color: 'var(--bui-fg-primary)',
        },
        filledError: {
          backgroundColor: 'var(--bui-bg-danger)',
          color: 'var(--bui-fg-primary)',
        },
        filledInfo: {
          backgroundColor: 'var(--bui-bg-solid)',
          color: 'var(--bui-fg-solid)',
        },
        filledWarning: {
          backgroundColor: 'var(--bui-bg-warning)',
          color: 'var(--bui-fg-primary)',
        },
        outlinedSuccess: {
          backgroundColor: 'var(--bui-bg-success)',
          color: 'var(--bui-fg-primary)',
          borderColor: 'var(--bui-border-success)',
        },
        outlinedError: {
          backgroundColor: 'var(--bui-bg-danger)',
          color: 'var(--bui-fg-primary)',
          borderColor: 'var(--bui-border-danger)',
        },
        outlinedInfo: {
          backgroundColor: 'var(--bui-bg-surface-1)',
          color: 'var(--bui-fg-primary)',
          borderColor: 'var(--bui-border)',
        },
        outlinedWarning: {
          backgroundColor: 'var(--bui-bg-warning)',
          color: 'var(--bui-fg-primary)',
          borderColor: 'var(--bui-border-warning)',
        },
      },
      MuiAlertTitle: {
        root: {
          fontWeight: 600,
        },
      },
      MuiPaper: {
        root: {
          boxShadow: 'none',
          '[role="tooltip"] &': {
            backgroundColor: 'var(--bui-bg-surface-1)',
          },
          '&.TimelineActivityPaper': {
            padding: 'var(--bui-space-1)',
          },
        },
        rounded: {
          borderRadius: 'var(--bui-radius-3)',
        },
        outlined: {
          // border: '1px solid var(--bui-border)',
          border: 'none',
          backgroundColor:
            theme.palette.type === 'dark' ? 'var(--bui-bg-tint)' : '#f4f4f4',
          transition: theme.transitions.create('border-color', {
            duration: theme.transitions.duration.shortest,
          }),
          '&:has(a[role="button"]:only-child):hover': {
            borderColor: 'var(--bui-border-hover)',
          },
        },
      },
      MuiCardHeader: {
        root: {
          padding: 'var(--bui-space-4)',
          paddingTop: 'var(--bui-space-2)',
          paddingBottom: 0,
          '&:only-child:last-child': {
            paddingBottom: 'var(--bui-space-4)',
          },
        },
      },
      MuiCardContent: {
        root: {
          padding: 'var(--bui-space-4)',
          '&:last-child': {
            // paddingBottom: theme.spacing(4),
            paddingBottom: 'var(--bui-space-4)',
          },
          '&:empty': {
            paddingBottom: 0,
          },
        },
      },
      MuiCardActionArea: {
        focusHighlight: {
          backgroundColor: 'transparent',
        },
        root: {
          '&.Mui-disabled': {
            opacity: 0.3,
            pointerEvents: 'none',
          },
        },
      },
      MuiListItem: {
        root: {
          paddingTop: 'var(--bui-space-1)',
          paddingBottom: 'var(--bui-space-1)',
        },
        gutters: {
          paddingLeft: 'var(--bui-space-3)',
          paddingRight: 'var(--bui-space-3)',
        },
        button: {
          position: 'relative',
          zIndex: 0,
          '&:hover': {
            backgroundColor: 'transparent',
            '&:after': {
              backgroundColor: 'var(--bui-bg-tint-hover)',
            },
          },
          '&:after': {
            content: '""',
            position: 'absolute',
            transitionDuration: theme.transitions.duration.shorter,
            borderRadius: 'var(--bui-radius-2)',
            zIndex: -1,
            border: 'none',
            top: 0,
            left: 0,
            bottom: 0,
            right: 0,
            height: '100%',
            width: '100%',
          },
          '&.Mui-selected': {
            '&:hover': {
              backgroundColor: 'transparent',
              '&:after': {
                backgroundColor: 'var(--bui-bg-tint-hover)',
              },
            },
            backgroundColor: 'transparent',
            '&:after': {
              backgroundColor: 'var(--bui-bg-tint-pressed)',
            },
          },
        },
      },
      MuiMenuItem: {
        root: {
          height: '40px !important',
        },
      },
      MuiListItemIcon: {
        root: {
          color: 'var(--bui-fg-primary)',
          minWidth: 'var(--bui-space-8)',
        },
      },
      MuiListItemText: {
        primary: {
          color: 'var(--bui-fg-primary)',
        },
      },
      MuiListItemSecondaryAction: {
        root: {
          right: 10,
          ':not(.MuiListItem-gutters) + &': {
            right: 0,
          },
        },
      },
      MuiBreadcrumbs: {
        separator: {
          color: 'var(--bui-fg-secondary)',
        },
        li: {
          textDecoration: 'none',
          '&:not(:last-child)': {
            color: 'var(--bui-fg-secondary)',
          },
          '&:last-child > div *': {
            fontStyle: 'normal',
          },
        },
      },
      MuiTooltip: {
        tooltip: {
          fontSize: 'var(--bui-font-size-3)',
          backgroundColor: 'var(--bui-bg-surface-1)',
          border: '1px solid var(--bui-gray-3)',
          color: 'var(--bui-fg-primary)',
          borderRadius: 'var(--bui-radius-2)',
          padding: 'var(--bui-space-2) var(--bui-space-3)',
        },
        arrow: {
          color: 'var(--bui-bg-surface-1)',
          '&::before': {
            border: '1px solid var(--bui-gray-3)',
          },
        },
      },
      MuiTypography: {
        colorPrimary: {
          color: 'var(--bui-fg-primary)',
        },
      },
      MuiButtonBase: {
        root: {
          transition: theme.transitions.create(
            ['background-color', 'border-color', 'color', 'box-shadow'],
            { duration: '150ms' },
          ),
          '&:focus-visible': {
            outline: '2px solid var(--bui-ring)',
            outlineOffset: '2px',
          },
        },
        disabled: {
          '&$root': {
            cursor: 'not-allowed',
          },
        },
      },
      MuiButton: {
        root: {
          borderRadius: 'var(--bui-radius-3)',
          padding: '0 var(--bui-space-3)',
          height: '2.5rem',
          position: 'relative',
          textTransform: 'none',
          fontWeight: 600,
          fontSize: 'var(--bui-font-size-3)', // body-large for medium button
          transition: theme.transitions.create(
            ['background-color', 'border-color', 'color', 'box-shadow'],
            { duration: '150ms' },
          ),
          '&:hover': {
            boxShadow: 'none',
          },
          '&:active': {
            boxShadow: 'none',
          },
          minWidth: 'auto',
        },
        contained: {
          // Do not set colors here to avoid forcing primary styles on all contained buttons.
          // Color-specific variants below (containedPrimary/containedSecondary) will handle appearance.
          boxShadow: 'none',
        },
        containedPrimary: primaryButton,
        containedSecondary: secondaryButton,
        outlined: secondaryButton,
        outlinedPrimary: secondaryButton,
        outlinedSecondary: secondaryButton,
        text: tertiaryButton,
        textPrimary: tertiaryButton,
        textSecondary: tertiaryButton,
        sizeLarge: {
          height: '2.5rem',
          padding: '0 var(--bui-space-3)',
          fontSize: 'var(--bui-font-size-4)', // body-large
        },
        sizeSmall: {
          height: '2rem',
          padding: '0 var(--bui-space-3)',
          fontSize: 'var(--bui-font-size-2)', // body-medium
          borderRadius: 'var(--bui-radius-3)',
        },
        startIcon: {
          marginRight: 'var(--bui-space-1_5)',
        },
        endIcon: {
          marginLeft: 'var(--bui-space-1_5)',
        },
        colorInherit: {
          '&.Mui-disabled': {
            color: 'inherit',
          },
        },
      },
      MuiIconButton: {
        root: {
          transition: theme.transitions.create(
            ['background-color', 'border-color', 'color'],
            { duration: '150ms' },
          ),
          color: 'var(--bui-fg-secondary)',
          '&:hover': {
            backgroundColor: 'var(--bui-bg-tint-hover)',
          },
          '&:active': {
            backgroundColor: 'var(--bui-bg-tint-pressed)',
          },
          '&:focus-visible': {
            outline: '2px solid var(--bui-ring)',
            outlineOffset: '2px',
          },
          width: 40,
          height: 40,
        },
        sizeSmall: {
          width: 32,
          height: 32,
          '& svg': {
            width: 16,
            height: 16,
          },
        },
        label: {
          width: 20,
          height: 20,
          '& svg': {
            width: 20,
            height: 20,
          },
        },
        edgeStart: {
          marginLeft: 0,
        },
        disabled: {
          '&$root': {
            color: 'var(--bui-fg-disabled)',
          },
        },
        colorPrimary: {
          color: 'var(--bui-fg-primary)',
          '&:hover': {
            backgroundColor: 'var(--bui-bg-surface-2)',
          },
        },
        colorSecondary: {
          color: 'var(--bui-fg-secondary)',
          '&:hover': {
            backgroundColor: 'var(--bui-bg-surface-2)',
          },
        },
        colorInherit: {
          color: 'inherit',
          '&:hover': {
            backgroundColor: 'var(--bui-bg-tint-hover)',
          },
        },
      },
      MuiButtonGroup: {
        root: {},
        grouped: {
          padding: 'var(--bui-space-2) var(--bui-space-5)',
          borderRadius: 'var(--bui-radius-2)',
          '&:hover': {
            transform: 'scale(1)',
          },
          '&.Mui-disabled': {
            color: 'var(--bui-fg-disabled)',
          },
        },
        contained: {
          boxShadow: 'none',
        },
        groupedContained: {
          border: '1px solid var(--bui-border)',
          backgroundColor: 'var(--bui-bg-surface-1)',
          color: 'var(--bui-fg-primary)',
          '&:hover, &:active': {
            backgroundColor: 'var(--bui-bg-surface-2)',
            borderColor: 'var(--bui-border)',
          },
          '&:not(:last-child)': {
            borderColor: 'var(--bui-border)',
          },
          '&.Mui-disabled': {
            backgroundColor: 'var(--bui-bg-surface-1)',
            color: 'var(--bui-fg-disabled)',
            borderColor: 'var(--bui-border)',
          },
        },
        groupedOutlined: {
          borderColor: 'var(--bui-border)',
          '&:hover': {
            borderColor: 'var(--bui-border-hover)',
          },
          '&.Mui-disabled': {
            borderColor: 'var(--bui-border-disabled)',
          },
        },
        groupedText: {
          color: 'var(--bui-fg-primary)',
          padding: 'var(--bui-space-2) var(--bui-space-3)',
          '&:hover': {
            backgroundColor: 'var(--bui-bg-surface-2)',
          },
          '&:not(:last-child)': {
            borderColor: 'var(--bui-border)',
          },
        },
      },
      MuiFab: {
        root: {
          backgroundColor: 'var(--bui-bg-solid)',
          color: 'var(--bui-fg-solid)',
          boxShadow: 'none',
          padding: 'var(--bui-space-4)',
          '&:hover': {
            backgroundColor: 'var(--bui-bg-solid-hover)',
            boxShadow: 'none',
          },
          '&:active, &:active:hover': {
            backgroundColor: 'var(--bui-bg-solid-pressed)',
            boxShadow: 'none',
          },
        },
        label: {
          display: 'inline-flex',
          gap: 'var(--bui-space-2)',
        },
        disabled: {
          '&$root': {
            backgroundColor: 'var(--bui-bg-solid-disabled)',
            color: 'var(--bui-fg-solid-disabled)',
          },
        },
      },
      MuiCheckbox: {
        root: {
          '&, &.Mui-disabled, &:active, &:hover': {
            backgroundColor: 'transparent',
          },
          '&:focus-visible': {
            outline: '2px solid var(--bui-ring)',
            outlineOffset: '2px',
          },
        },
        disabled: {
          '&$root': {
            opacity: 1,
          },
        },
        colorPrimary: {
          color: 'var(--bui-border)',
          '&:hover': {
            color: 'var(--bui-border-hover)',
          },
          '&.Mui-checked': {
            color: 'var(--bui-bg-solid)',
          },
          '&.Mui-disabled': {
            color: 'var(--bui-border-disabled)',
          },
        },
        colorSecondary: {
          color: 'var(--bui-border-pressed)',
          '&:hover': {
            color: 'var(--bui-border-hover)',
          },
          '&.Mui-checked': {
            color: 'var(--bui-bg-solid)',
          },
          '&.Mui-disabled': {
            color: 'var(--bui-border-disabled)',
          },
        },
      },
      MuiRadio: {
        root: {
          '&, &.Mui-disabled, &:active, &:hover': {
            backgroundColor: 'transparent',
          },
          '&:focus-visible': {
            outline: '2px solid var(--bui-ring)',
            outlineOffset: '2px',
          },
        },
        disabled: {
          '&$root': {
            opacity: 0.5,
          },
        },
        colorPrimary: {
          color: 'var(--bui-border)',
          '&:hover': {
            color: 'var(--bui-border-hover)',
          },
          '&.Mui-checked': {
            color: 'var(--bui-bg-solid)',
          },
          '&.Mui-disabled': {
            color: 'var(--bui-border-disabled)',
          },
        },
        colorSecondary: {
          color: 'var(--bui-border)',
          '&:hover': {
            color: 'var(--bui-border-hover)',
          },
          '&.Mui-checked': {
            color: 'var(--bui-bg-solid)',
          },
          '&.Mui-disabled': {
            color: 'var(--bui-border-disabled)',
          },
        },
      },
      MuiTableBody: {
        root: {
          '& > tr:last-of-type, & > tr:last-child, & > [role=row]:last-of-type, & > [role=row]:last-child':
            {
              '& [role=cell], & td': {
                borderBottom: 'none',
              },
            },
        },
      },
      MuiTableRow: {
        root: {
          color: 'var(--bui-fg-primary)',
          transition: theme.transitions.create('background-color', {
            duration: '100ms',
          }),
          marginBottom: 1,
        },
        hover: {
          '&$hover:hover': {
            backgroundColor: 'var(--bui-bg-tint-hover)',
          },
        },
        selected: {
          '&$root': {
            backgroundColor: 'var(--bui-bg-tint-pressed)',
          },
        },
      },
      MuiTableContainer: {
        root: {
          border: 'none',
          backgroundColor: theme.palette.background.default,
        },
      },
      MuiTableCell: {
        head: {
          backgroundColor: theme.palette.background.default,

          borderBottom: '1px solid var(--bui-border)',
          color: 'var(--bui-fg-secondary)',
          ...theme.typography.subtitle1,
          fontSize: 'var(--bui-font-size-2)',
          fontWeight: 400,
        },
        body: {
          backgroundColor: theme.palette.background.default,
          '&:first-of-type': {
            borderTopLeftRadius: 'var(--bui-radius-2)',
            borderBottomLeftRadius: 'var(--bui-radius-2)',
          },
          '&:last-of-type': {
            borderTopRightRadius: 'var(--bui-radius-2)',
            borderBottomRightRadius: 'var(--bui-radius-2)',
          },
          borderBottom: 'none', // borderBottom: '1px solid var(--bui-border)',
          color: 'var(--bui-fg-primary)',
          ...theme.typography.body2,
          '& span': {
            fontSize: 'var(--bui-font-size-3)',
          },
          '& [class*="StateIcon-root"]': {
            marginTop: '2px !important', // Adjust the state icon position
          },
          'tr:hover > &': {
            backgroundColor: 'var(--bui-bg-tint-hover)',
          },
        },
        root: {
          padding: 'var(--bui-space-3)',
          borderRadius: '0 !important',
        },
      },
      MuiLink: {
        root: {
          color: 'inherit',
          textDecoration: 'none',
          '&:hover': {
            textDecoration: 'underline',
            textDecorationColor:
              'color-mix(in srgb, currentColor 30%, transparent)',
          },
        },
        underlineAlways: {
          '&:hover': {
            color: 'var(--bui-fg-link-hover)',
          },
        },
      },
      MuiRating: {
        iconFocus: {
          transform: 'scale(1)',
        },
        iconHover: {
          transform: 'scale(1.04)',
        },
        iconEmpty: {
          color: 'var(--bui-fg-secondary)',
        },
        iconFilled: {
          color: 'var(--bui-fg-warning)',
        },
        iconActive: {
          color: 'var(--bui-fg-warning)',
        },
      },
      MuiSwitch: {
        root: {
          '&:has(.Mui-disabled)': {
            opacity: 0.5,
          },
        },
        switchBase: {
          marginLeft: 0,
          padding: 12,
          pointerEvents: 'auto',

          '&:hover': {
            backgroundColor: 'transparent',

            '& + $track': {
              backgroundColor: 'var(--bui-border-pressed)',
            },
          },
          '&.Mui-disabled': {
            '& + $track': {
              backgroundColor: 'var(--bui-bg-tint-pressed)',
              opacity: 0.5,
            },
          },
          '&.Mui-checked': {
            backgroundColor: 'transparent',
            color: 'var(--bui-fg-solid)',
            transform: 'translateX(12px)',

            '& + $track': {
              backgroundColor: 'var(--bui-bg-solid)',
              opacity: 1,
            },
            '&:hover': {
              backgroundColor: 'transparent !important',

              '& + $track': {
                backgroundColor: 'var(--bui-bg-solid-pressed) !important',
              },
            },
            '&.Mui-disabled': {
              '& + $track': {
                backgroundColor: 'var(--bui-bg-solid)',
                opacity: 0.5,
              },
            },
          },
        },
        thumb: {
          backgroundColor: 'var(--bui-fg-solid)',
          boxShadow: 'none',
          width: 12,
          height: 12,
          top: -1,
          position: 'relative',
        },
        track: {
          backgroundColor: 'var(--bui-gray-6)',
          opacity: 1,
          width: 28,
        },
      },
      MuiInputBase: {
        inputMarginDense: {
          paddingTop: 'var(--bui-space-3)',
          paddingBottom: 'var(--bui-space-3)',
        },
        input: {
          padding: 'var(--bui-space-5) var(--bui-space-4)',
          color: 'var(--bui-fg-primary)',
          '&::placeholder': {
            color: 'var(--bui-fg-secondary)',
          },
        },
        inputMultiline: {
          padding: 'var(--bui-space-5) var(--bui-space-4)',
        },
      },
      MuiInput: {
        underline: {
          '&:before, &:after': {
            display: 'none',
          },
        },
        formControl: {
          'label + &': {
            marginTop: 0,
          },
        },
        root: {
          border: '1px solid var(--bui-border)',
          borderRadius: 'var(--bui-radius-2)',
          backgroundColor: 'var(--bui-bg-surface-1)',
          transition: theme.transitions.create('border-color', {
            duration: '0.2s',
          }),
          '&:hover': {
            borderColor: 'var(--bui-border-hover)',
          },
          '&.Mui-focused': {
            borderColor: 'var(--bui-border-pressed)',
          },
          '&.Mui-error': {
            borderColor: 'var(--bui-fg-danger)',
          },
          '&.Mui-disabled': {
            opacity: 0.5,
            borderColor: 'var(--bui-border-disabled)',
          },
        },
      },
      MuiOutlinedInput: {
        root: {
          backgroundColor: 'var(--bui-bg-surface-1)',
          '& $notchedOutline': {
            borderColor: 'var(--bui-border)',
          },
          '&:hover $notchedOutline': {
            borderColor: 'var(--bui-border-hover)',
          },
          '&.Mui-focused $notchedOutline': {
            borderColor: 'var(--bui-border-pressed)',
            borderWidth: 1,
          },
          '&.Mui-error $notchedOutline': {
            borderColor: 'var(--bui-fg-danger)',
          },
          '&.Mui-disabled': {
            opacity: 0.5,
            '& $notchedOutline': {
              borderColor: 'var(--bui-border-disabled)',
            },
          },
        },
        notchedOutline: {
          borderRadius: 'var(--bui-radius-2)',
        },
      },
      PrivateNotchedOutline: {
        legendLabelled: {
          width: 0,
        },
      },
      MuiFilledInput: {
        root: {
          backgroundColor: 'var(--bui-bg-surface-1)',
          border: '1px solid var(--bui-border)',
          borderRadius: 'var(--bui-radius-2)',
          '&:hover': {
            backgroundColor: 'var(--bui-bg-surface-1)',
            borderColor: 'var(--bui-border-hover)',
          },
          '&.Mui-focused': {
            backgroundColor: 'var(--bui-bg-surface-1)',
            borderColor: 'var(--bui-border-pressed)',
          },
          '&.Mui-error': {
            borderColor: 'var(--bui-fg-danger)',
          },
          '&.Mui-disabled': {
            opacity: 0.5,
            backgroundColor: 'var(--bui-bg-surface-1)',
          },
        },
        underline: {
          '&:before, &:after': {
            display: 'none',
          },
        },
      },
      MuiSelect: {
        icon: {
          right: 7,
          color: 'var(--bui-fg-secondary)',
        },
        select: {
          borderRadius: 'var(--bui-radius-2)',
          backgroundColor: 'transparent',
          '&:focus': {
            borderRadius: 'var(--bui-radius-2)',
            backgroundColor: 'transparent',
          },
          '&$select': {
            paddingRight: 'var(--bui-space-8)',
          },
        },
      },
      MuiFormLabel: {
        root: {
          ...theme.typography.subtitle2,
          fontWeight: 600,
          color: 'var(--bui-fg-primary)',
          '&.Mui-focused': {
            color: 'var(--bui-fg-primary)',
          },
          '&.Mui-error': {
            color: 'var(--bui-fg-danger)',
          },
        },
        asterisk: {
          color: 'var(--bui-fg-danger)',
        },
      },
      MuiFormGroup: {
        root: {
          gap: theme.spacing(1),
        },
      },
      MuiFormControl: {
        root: {
          gap: 'var(--bui-space-2)',
        },
      },
      MuiFormControlLabel: {
        label: {
          paddingLeft: theme.spacing(0.5),
          '$root.Mui-disabled &': {
            color: 'var(--bui-fg-secondary)',
            opacity: 0.8,
          },
        },
      },
      MuiInputLabel: {
        root: {
          ...theme.typography.subtitle2,
          fontWeight: 600,
          color: 'var(--bui-fg-primary)',
        },
        asterisk: {
          color: 'var(--bui-fg-danger)',
        },
        shrink: {
          transform: 'none',
        },
        marginDense: {
          transform: 'none',
        },
        outlined: {
          pointerEvents: 'all',
          '&, &$shrink, &$marginDense': {
            transform: 'none',
          },
        },
        filled: {
          pointerEvents: 'all',
          '&, &$shrink, &$marginDense, &$root$shrink$marginDense': {
            transform: 'none',
          },
        },
        formControl: {
          position: 'relative',
          transform: 'none',
        },
      },
      MuiFormHelperText: {
        root: {
          color: 'var(--bui-fg-secondary)',
          fontSize: 'var(--bui-font-size-2)',
        },
        contained: {
          marginLeft: 0,
          marginRight: 0,
        },
      },
      MuiInputAdornment: {
        marginDense: {
          '& .MuiIconButton-root': {
            padding: 6,
          },
        },
      },
      MuiAvatar: {
        colorDefault: {
          backgroundColor: 'var(--bui-gray-4)',
        },
      },
      MuiAvatarGroup: {
        avatar: {
          borderColor: 'var(--bui-bg-surface-1)',
        },
      },
      MuiToggleButtonGroup: {
        groupedHorizontal: {
          '&:not(:first-child)': {
            marginLeft: 0,
          },
          '&.Mui-selected': {
            '&:not(:first-child)': {
              borderLeft: '1px solid var(--bui-border-pressed)',
            },
            '&:not(:last-child)': {
              borderRight: '1px solid var(--bui-border-pressed)',
            },
          },
        },
      },
      MuiSlider: {
        root: {
          color: 'var(--bui-bg-solid)',
        },
        rail: {
          backgroundColor: 'var(--bui-gray-3)',
        },
        thumbColorPrimary: {
          '& $valueLabel': {
            color: 'var(--bui-bg-solid)',
          },
        },
      },
      MuiToggleButton: {
        root: {
          backgroundColor: 'var(--bui-bg-surface-1)',
          borderColor: 'var(--bui-border)',
          color: 'var(--bui-fg-primary)',
          '&:hover': {
            backgroundColor: 'var(--bui-bg-surface-2)',
          },
          '&$disabled': {
            color: 'var(--bui-fg-disabled)',
          },
          '&.Mui-selected': {
            backgroundColor: 'var(--bui-bg-solid)',
            color: 'var(--bui-fg-solid)',
            '&$disabled': {
              color: 'var(--bui-fg-solid-disabled)',
            },
            '$root&:hover': {
              backgroundColor: 'var(--bui-bg-solid-hover)',
            },
          },
        },
      },
      MuiSkeleton: {
        root: {
          backgroundColor: 'var(--bui-bg-neutral-2)',
        },
      },
      MuiChip: {
        root: {
          height: '2rem',
          borderRadius: 'var(--bui-radius-2)',
          fontSize: 'var(--bui-font-size-2)',
          backgroundColor: 'var(--bui-bg-surface-1)',
          color: 'var(--bui-fg-primary)',
          transition: theme.transitions.create(
            ['background-color', 'opacity'],
            {
              duration: '0.1s',
            },
          ),
        },
        outlined: {
          borderColor: 'var(--bui-border-hover)',
        },
        outlinedPrimary: {
          borderColor: 'var(--bui-bg-solid)',
          color: 'var(--bui-bg-solid)',
        },
        deleteIcon: {
          height: '1rem !important',
          color: 'var(--bui-fg-primary)',
          '&:hover': {
            color: 'var(--bui-fg-secondary)',
          },
        },
        clickable: {
          '&:active': {
            boxShadow: 'none',
          },
        },
        sizeSmall: {
          height: '1.5rem',
        },
        avatar: {
          '$root &': {
            backgroundColor: 'var(--bui-bg-solid)',
            color: 'var(--bui-fg-solid)',
          },
        },
      },
      MuiTreeItem: {
        root: {
          '&.Mui-selected > .MuiTreeItem-content .MuiTreeItem-label': {
            backgroundColor: 'var(--bui-bg-tint-pressed)',
          },
        },
      },
      MuiMenu: {
        paper: {
          borderRadius: 'var(--bui-radius-2)',
          marginTop: 'var(--bui-space-2)',
          border: '1px solid var(--bui-border)',
        },
      },
      MuiAutocomplete: {
        groupLabel: {
          backgroundColor: 'var(--bui-bg-surface-1)',
        },
        inputRoot: {
          '&[class*="MuiInput-root"] input$input': {
            padding: 'var(--bui-space-5) var(--bui-space-4)',
            '&:first-child': {
              padding: 'var(--bui-space-5) var(--bui-space-4)',
            },
          },
        },
        endAdornment: {
          top: 'unset',
        },
        paper: {
          border: '1px solid var(--bui-border)',
          borderRadius: 'var(--bui-radius-2)',
          '&:empty': {
            border: 'none',
          },
        },
        clearIndicator: {
          // marginRight: 'var(--bui-space-2)',
        },
        popupIndicator: {
          // marginRight: 'var(--bui-space-2)',
        },
        option: {
          paddingLeft: 'var(--bui-space-3)',
        },
      },
      MuiStepper: {
        horizontal: {
          gap: 'var(--bui-space-4)',
        },
      },
      MuiStepConnector: {
        root: {
          '&.Mui-disabled': {
            opacity: 1,
          },
        },
        lineHorizontal: {
          borderColor: 'var(--bui-fg-secondary)',
        },
      },
      MuiStepLabel: {
        label: {
          color: 'var(--bui-fg-secondary)',
          '&$completed': {
            color: 'var(--bui-fg-secondary)',
          },
          '&$completed$active': {
            color: 'var(--bui-fg-primary)',
            fontWeight: 600,
          },
          '&$active': {
            color: 'var(--bui-fg-primary)',
            fontWeight: 600,
          },
        },
      },
      MuiTabs: {
        indicator: {
          display: 'flex',
          justifyContent: 'center',
          height: 1,
          backgroundColor: 'var(--bui-fg-primary)',
        },
        flexContainer: {
          gap: theme.spacing(2),
        },
      },
      MuiTab: {
        root: {
          fontWeight: 400,
          fontSize: 'var(--bui-font-size-3)',
          textTransform: 'none',
          color: 'var(--bui-fg-secondary)',
          padding: 0,
          [theme.breakpoints.up('sm')]: {
            minWidth: 'auto',
          },
          '&[data-testid*="header-tab"]': {
            marginRight: theme.spacing(4),
          },
        },
        textColorPrimary: {
          '&.Mui-selected': {
            color: 'var(--bui-fg-primary)',
          },
        },
      },
      MuiTabPanel: {
        root: {
          padding: theme.spacing(0, 0),
          '&:not([hidden])': {
            padding: theme.spacing(3, 0),
          },
        },
      },
      MuiAppBar: {
        root: {
          borderRadius: 0,
          '&$colorDefault, &$colorPrimary, &$colorInherit, &$colorTransparent':
            {
              backgroundColor: 'var(--bui-bg-surface-1)',
              color: 'var(--bui-fg-primary)',
            },
        },
      },
      MuiAccordion: {
        root: {
          '&.Mui-disabled': {
            opacity: 0.5,
          },
        },
      },
      MuiAccordionSummary: {
        root: {
          minHeight: 52,
          '&.Mui-expanded': {
            minHeight: 52,
          },
        },
        content: {
          margin: 'var(--bui-space-2) 0',
          '&.Mui-expanded': {
            margin: 'var(--bui-space-2) 0',
          },
        },
      },
      MuiPaginationItem: {
        outlined: {
          backgroundColor: 'var(--bui-bg-surface-1)',
          border: '1px solid var(--bui-border)',
          color: 'var(--bui-fg-primary)',
          '.Mui-selected&': {
            backgroundColor: 'var(--bui-bg-tint-pressed)',
            borderColor: 'var(--bui-border-pressed)',
          },
        },
        outlinedPrimary: {
          '&.Mui-selected': {
            backgroundColor: 'var(--bui-bg-tint-pressed)',
            borderColor: 'var(--bui-border)',
            color: 'var(--bui-fg-tint)',
          },
        },
      },
      MuiLinearProgress: {
        root: {
          height: 3,
          borderRadius: 'var(--bui-radius-2)',
        },
        bar: {
          borderRadius: 'var(--bui-radius-2)',
        },
        colorPrimary: {
          backgroundColor: 'var(--bui-gray-3)',
        },
        barColorPrimary: {
          backgroundColor: 'var(--bui-bg-solid)',
        },
      },
      MuiCircularProgress: {
        colorPrimary: {
          color: 'var(--bui-bg-solid)',
        },
      },
      MuiSnackbar: {
        root: {
          boxShadow: theme.shadows[1],
          borderRadius: 'var(--bui-radius-3)',
          zIndex: 2000,
        },
      },
      MuiSnackbarContent: {
        root: {
          backgroundColor: 'var(--bui-bg-surface-2)',
          color: 'var(--bui-fg-primary)',
        },
      },
      MuiSvgIcon: {
        fontSizeSmall: {
          fontSize: 'var(--bui-font-size-3)',
        },
        colorPrimary: {
          color: 'var(--bui-fg-primary)',
        },
      },
      MuiPickersCalendarHeader: {
        root: {
          color: 'var(--bui-fg-secondary)',
        },
        iconButton: {
          backgroundColor: 'transparent',
        },
      },
      MuiPickersToolbar: {
        toolbar: {
          backgroundColor: 'var(--bui-bg-solid)',
          '& h3': {
            fontSize: '2em',
          },
        },
      },
      MuiPickersToolbarText: {
        toolbarTxt: {
          color: 'var(--bui-fg-solid)',
        },
        toolbarBtnSelected: {
          color: 'var(--bui-fg-solid)',
        },
      },
      MuiPickersTimePickerToolbar: {
        ampmLabel: {
          opacity: 0.3,
          '&.MuiPickersToolbarText-toolbarBtnSelected': {
            opacity: 1,
          },
        },
      },
      MuiPickersClock: {
        clock: {
          backgroundColor: theme.palette.background.default,
        },
      },
      MuiPickersDay: {
        current: {
          color: 'var(--bui-fg-tint)',
        },
      },
      MuiPickersBasePicker: {
        pickerView: {
          overflowY: 'hidden',
        },
      },
      MuiDialogTitle: {
        root: {
          // padding: theme.spacing(1, 3),
          padding: 'var(--bui-space-3)',
          borderBottom: '1px solid var(--bui-border)',

          // override font
          '& > *': {
            fontSize: 'var(--bui-font-size-3)',
          },
          '& .MuiTypography-h4': {
            fontSize: 'var(--bui-font-size-3)',
          },
          // Close button
          '& > button': {
            padding: 'var(--bui-space-1)',
            fontSize: '1rem',
            top: 2,
            right: 2,
          },
          '& .MuiSvgIcon-root': {
            fontSize: '1rem',
          },
        },
      },
      MuiDialog: {
        paper: {
          background: theme.palette.background.default,
          backgroundColor: theme.palette.background.default,
          padding: 0, // 'var(--bui-space-3)',
          /*
          [theme.breakpoints.up('sm')]: {
            padding: 'var(--bui-space-8)',
          },
          [theme.breakpoints.up('md')]: {
            padding: 'var(--bui-space-14)',
          },
          */
          borderRadius: 'var(--bui-radius-3)',
          border: '1px solid var(--bui-border)',
        },
        paperFullScreen: {
          background: theme.palette.background.default,
          backgroundColor: theme.palette.background.default,
        },
      },
      MuiDialogContent: {
        root: {
          padding: 'var(--bui-space-3) !important',
        },
        dividers: {
          '& + *': {
            marginTop: theme.spacing(2),
          },
        },
      },
      MuiDialogActions: {
        root: {
          padding: 'var(--bui-space-3)',
          // marginTop: theme.spacing(4),
          borderTop: '1px solid var(--bui-border)',
        },
      },
      MuiPopover: {
        paper: {
          // background: 'var(--bui-bg-surface-1)',
          borderRadius: 'var(--bui-radius-2)',
          border: '1px solid var(--bui-border)',
        },
      },
      MuiBottomNavigationAction: {
        root: {
          '&.Mui-selected': {
            borderTopColor: 'var(--bui-bg-solid)',
          },
        },
      },
      RuleFormInputGrid: {
        label: {
          paddingTop: 'var(--bui-space-3) !important',
        },
      },
      CommentsSidebar: {
        root: {
          paddingTop: '64px !important',
        },
      },
    };
  };

  return {
    ...baseTheme,
    shape: {
      borderRadius: 4, // --bui-radius-2 equivalent
    },
    typography: {
      ...baseTheme.typography,
      fontFamily: 'var(--encore-body-font-stack)',
      // MUI → BUI Typography Mapping:
      // h1 → title-large (48px)
      h1: {
        fontFamily: 'var(--encore-title-font-stack)',
        fontSize: 'var(--bui-font-size-8)',
        fontWeight: 600,
        lineHeight: 1.4,
      },
      // h2 → title-medium (32px)
      h2: {
        fontFamily: 'var(--encore-title-font-stack)',
        fontSize: 'var(--bui-font-size-7)',
        fontWeight: 600,
        lineHeight: 1.4,
      },
      // h3 → title-small (24px)
      h3: {
        fontFamily: 'var(--encore-title-font-stack)',
        fontSize: 'var(--bui-font-size-6)',
        fontWeight: 600,
        lineHeight: 1.4,
      },
      // h4 → title-x-small (20px)
      h4: {
        fontFamily: 'var(--encore-title-font-stack)',
        fontSize: 'var(--bui-font-size-5)',
        fontWeight: 600,
        lineHeight: 1.4,
      },
      // h5 → body-large bold (16px)
      h5: {
        fontFamily: 'var(--encore-title-font-stack)',
        fontSize: 'var(--bui-font-size-4)',
        fontWeight: 600,
        lineHeight: 1.4,
      },
      // h6 → body-medium bold (14px)
      h6: {
        fontFamily: 'var(--encore-title-font-stack)',
        fontSize: 'var(--bui-font-size-3)',
        fontWeight: 600,
        lineHeight: 1.4,
      },
      // body1 → body-medium (14px) - default body text
      body1: {
        fontFamily: 'var(--bui-font-regular)',
        fontSize: 'var(--bui-font-size-3)',
        fontWeight: 400,
        lineHeight: 1.4,
      },
      // body2 → body-small (12px)
      body2: {
        fontFamily: 'var(--bui-font-regular)',
        fontSize: 'var(--bui-font-size-2)',
        fontWeight: 400,
        lineHeight: 1.4,
      },
      // caption → body-x-small (10px)
      caption: {
        fontFamily: 'var(--bui-font-regular)',
        fontSize: 'var(--bui-font-size-1)',
        fontWeight: 400,
        lineHeight: 1.4,
      },
      // subtitle1 → body-medium bold (14px)
      subtitle1: {
        fontFamily: 'var(--bui-font-regular)',
        fontSize: 'var(--bui-font-size-3)',
        fontWeight: 600,
        lineHeight: 1.4,
      },
      // subtitle2 → body-medium bold (14px)
      subtitle2: {
        fontFamily: 'var(--bui-font-regular)',
        fontSize: 'var(--bui-font-size-3)',
        fontWeight: 600,
        lineHeight: 1.4,
      },
      // button → body-medium bold (14px)
      button: {
        fontFamily: 'var(--bui-font-regular)',
        fontSize: 'var(--bui-font-size-3)',
        fontWeight: 600,
        lineHeight: 1.4,
      },
      // overline → body-x-small bold uppercase (10px)
      overline: {
        fontFamily: 'var(--bui-font-regular)',
        fontSize: 'var(--bui-font-size-1)',
        fontWeight: 600,
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
        lineHeight: 1.4,
      },
    },
    props: {
      ...baseTheme.props,
      MuiButtonBase: {
        disableRipple: true,
      },
      MuiButtonGroup: {
        disableRipple: true,
      },
      MuiTableSortLabel: {
        IconComponent: ArrowDropDownIcon,
      },
      MuiPaper: {
        variant: 'outlined',
      },
      MuiTextField: {
        margin: 'dense',
        InputLabelProps: {
          // Always show the label
          shrink: true,
        },
      },
      MuiBreadcrumbs: {
        separator: <IconChevronRight />,
      },
      MuiSelect: {
        MenuProps: {
          PaperProps: {
            variant: 'elevation',
          },
        },
        margin: 'dense',
        IconComponent: KeyboardArrowDownIcon,
      },
      MuiInputLabel: {
        margin: 'dense',
      },
      MuiFormControl: {
        margin: 'dense',
      },
      MuiFormHelperText: {
        margin: 'dense',
      },
      MuiMenu: {
        getContentAnchorEl: null,
        PaperProps: {
          variant: 'elevation',
        },
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'left',
        },
        transformOrigin: {
          vertical: 'top',
          horizontal: 'left',
        },
      },
      MuiAutocomplete: {
        PaperComponent: props => <Paper {...props} variant="elevation" />,
        popupIcon: <KeyboardArrowDownIcon />,
      },
      MuiList: {
        disablePadding: true,
      },
      MuiPopover: {
        PaperProps: {
          variant: 'elevation',
        },
      },
      MuiDialog: {
        PaperProps: {
          variant: 'elevation',
        },
      },
      MuiDrawer: {
        PaperProps: {
          variant: 'elevation',
        },
      },
      MuiLink: {
        color: 'inherit',
        underline: 'hover',
      },
      MuiAccordion: {
        variant: 'elevation',
      },
      MuiSnackbarContent: {
        variant: 'elevation',
        elevation: 1,
      },
    },
    overrides: {
      ...baseTheme.overrides,
      // This unfortunatley get typography from "baseTheme" which is the MUI theme
      // and not our new theme.
      ...createCustomThemeOverrides(baseTheme),
      MuiCssBaseline: {
        '@global': {
          body: {
            fontFamily: 'var(--bui-font-regular)',
          },
        },
      },
    },
  };
};
